import { IconClose } from "@/assets/icons";
import { FeeDateCell, FeeNumberCell, FeePayerSelectCell, FeeTextCell } from "@/features/management-ledger/components/FeeLedgerEditableCells";
import { TFeeLedgerRow } from "@/features/management-ledger/types/management-ledger";
import { TOption } from "@/types/common";
import { IconButton } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";

export const FEE_LEDGER_PINNED_COLUMN_ID = "payerName";

const multiLineHeadProps = { sx: { "& .Mui-TableHeadCell-Content-Wrapper": { whiteSpace: "pre-line" as const } } };

export type TUpdateFeeLedgerRow = <K extends keyof TFeeLedgerRow>(rowKey: string, field: K, value: TFeeLedgerRow[K]) => void;
export type TRemoveFeeLedgerRow = (rowKey: string) => void;

export const getFeeLedgerColumns = (
    isEditing: boolean,
    updateRow: TUpdateFeeLedgerRow,
    removeRow: TRemoveFeeLedgerRow,
    payerOptions: TOption[],
    showValidationError?: boolean,
): MRT_ColumnDef<TFeeLedgerRow>[] => {
    const columns: MRT_ColumnDef<TFeeLedgerRow>[] = [
        {
            id: FEE_LEDGER_PINNED_COLUMN_ID,
            accessorKey: "payerName",
            header: "①手数料を支払う者の\n氏名又は名称",
            size: 240,
            muiTableHeadCellProps: multiLineHeadProps,
            Cell: ({ row }) => (
                <FeePayerSelectCell
                    value={row.original.payerCompanyId}
                    options={payerOptions}
                    isEditing={isEditing}
                    displayName={row.original.payerName}
                    onChange={(value, label) => {
                        updateRow(row.original.rowKey, "payerCompanyId", value);
                        updateRow(row.original.rowKey, "payerName", label);
                    }}
                />
            ),
        },
        {
            accessorKey: "collectionDate",
            header: "②徴収年月日",
            size: 140,
            muiTableBodyCellProps: { sx: { overflow: "visible" } },
            Cell: ({ row }) => (
                <FeeDateCell
                    value={row.original.collectionDate}
                    isEditing={isEditing}
                    showValidationError={showValidationError}
                    onChange={(next) => updateRow(row.original.rowKey, "collectionDate", next)}
                />
            ),
        },
        {
            accessorKey: "feeType",
            header: "③手数料の種類",
            size: 160,
            Cell: ({ row }) => (
                <FeeTextCell
                    value={row.original.feeType}
                    isEditing={isEditing}
                    onChange={(next) => updateRow(row.original.rowKey, "feeType", next)}
                />
            ),
        },
        {
            accessorKey: "feeAmount",
            header: "④手数料の額\n\n手数料（※）",
            size: 160,
            muiTableHeadCellProps: multiLineHeadProps,
            Cell: ({ row }) => (
                <FeeNumberCell
                    value={row.original.feeAmount}
                    isEditing={isEditing}
                    onChange={(next: string) => updateRow(row.original.rowKey, "feeAmount", next)}
                />
            ),
        },
        {
            accessorKey: "feeBasis",
            header: "⑤手数料の算出根拠\n\n賃金・割合等",
            size: 220,
            muiTableHeadCellProps: multiLineHeadProps,
            Cell: ({ row }) => (
                <FeeTextCell
                    value={row.original.feeBasis}
                    isEditing={isEditing}
                    onChange={(next) => updateRow(row.original.rowKey, "feeBasis", next)}
                />
            ),
        },
        {
            accessorKey: "remarks",
            header: "備考",
            size: 160,
            Cell: ({ row }) => (
                <FeeTextCell
                    value={row.original.remarks}
                    isEditing={isEditing}
                    onChange={(next) => updateRow(row.original.rowKey, "remarks", next)}
                />
            ),
        },
    ];

    if (isEditing) {
        columns.push({
            id: "feeRowActions",
            header: "",
            size: 56,
            enableSorting: false,
            Cell: ({ row }) => (
                <IconButton size="small" onClick={() => removeRow(row.original.rowKey)}>
                    <IconClose width={16} height={16} />
                </IconButton>
            ),
        });
    }

    return columns;
};
